import { z } from 'zod';

// The product-registration family's wire contracts with SBC. The endpoints live at
// `POST /api/agent-platform/product-registration/<name>` and are authored in SBC's
// `productRegistrationAgentContract.ts` (TRD §2.5/§2.6); these are the platform's copies of
// the halves the platform itself writes or reads back.
//
// Only the REQUEST bodies are pinned here. The two read endpoints hand their JSON straight to
// the model unchanged, so a snapshot schema here would be a second copy of a shape SBC owns —
// it would go stale the moment SBC adds a field, and narrowing a read to it would silently
// hide that field from the prompt. Same reason the run's `result` stays opaque (CLAUDE.md
// rule 10): what a job answers with is agreed between the agent's prompt and SBC's receiver.
// `record-thumbnail` is the exception — the platform acts on its answer, so that one is parsed.

// A body the platform received from SBC and passes on to the model without narrowing it.
export const ProductRegistrationReadResponseSchema = z.record(z.string(), z.unknown());

export const ProductRegistrationReadProductRequestSchema = z.object({
  // Comes from the job payload SBC sent, which the prompt tells the agent to quote back. The
  // scope check is SBC's: the platform holds no registration state to check it against.
  registration_id: z.uuid(),
});
export type ProductRegistrationReadProductRequest = z.infer<
  typeof ProductRegistrationReadProductRequestSchema
>;

export const HS_CODE_SEARCH_MAX_LIMIT = 20;

export const ProductRegistrationHsCodeSearchRequestSchema = z.object({
  query: z.string().min(1).max(200),
  limit: z.number().int().min(1).max(HS_CODE_SEARCH_MAX_LIMIT).optional(),
});
export type ProductRegistrationHsCodeSearchRequest = z.infer<
  typeof ProductRegistrationHsCodeSearchRequestSchema
>;

export const THUMBNAIL_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export type ThumbnailMimeType = (typeof THUMBNAIL_MIME_TYPES)[number];

// SBC's decoded cap on a recorded thumbnail (`MAX_THUMBNAIL_BYTES` in its agent contract); the
// platform fits a kept brand photo under it rather than have SBC refuse the write.
export const THUMBNAIL_MAX_BYTES = 3 * 1024 * 1024;

// The bound every one-sentence reason the platform ships to SBC is cut to, so the tools cut
// before they build a payload rather than failing the parse after the model has been paid for.
export const MAX_REASON_LENGTH = 300;

// The one direct write a run makes (TRD T15). Bytes do not belong in a jsonb outcome, so the
// image is posted to SBC as it is rendered rather than riding home with the result.
export const ProductRegistrationRecordThumbnailRequestSchema = z.object({
  registration_id: z.uuid(),
  thumbnail: z.object({
    mime_type: z.enum(THUMBNAIL_MIME_TYPES),
    // Base64, capped at 3 MB decoded by SBC, which sniffs the real type and answers 400
    // `bad_image` on a disagreement.
    data: z.string().min(1),
  }),
  // One of the brand's own `image_urls`, or null when no texture shot was picked.
  texture_shot_url: z.string().nullable(),
  self_check: z.object({ match: z.boolean(), reason: z.string().max(MAX_REASON_LENGTH) }),
});
export type ProductRegistrationRecordThumbnailRequest = z.infer<
  typeof ProductRegistrationRecordThumbnailRequestSchema
>;

// Parsed rather than passed through: the generate-thumbnail tool acts on these three values —
// it reports the CDN URL back to the run and surfaces `flagged` when SBC recorded a
// self-check mismatch — so a drift here must fail loudly instead of reaching a prompt.
export const ProductRegistrationRecordThumbnailResponseSchema = z.object({
  thumbnail_url: z.string(),
  texture_shot_url: z.string().nullable(),
  flagged: z.boolean(),
});
export type ProductRegistrationRecordThumbnailResponse = z.infer<
  typeof ProductRegistrationRecordThumbnailResponseSchema
>;

export const DOCUMENT_CHECK_VERDICTS = ['pass', 'fail', 'missing'] as const;
export const DocumentCheckVerdictSchema = z.enum(DOCUMENT_CHECK_VERDICTS);
export type DocumentCheckVerdict = z.infer<typeof DocumentCheckVerdictSchema>;

// What the document-check tool records about the file IT fetched — never a verdict the model
// relayed (CLAUDE.md rule 10). `source_ref` is the `msds_url` the tool read, and SBC compares
// it against the current one: a verdict about a file the brand has since replaced is stale and
// is skipped on apply, so the next `submission` run re-checks.
export const ProductRegistrationDocumentCheckSchema = z.object({
  registration_id: z.uuid(),
  document: z.literal('msds'),
  verdict: DocumentCheckVerdictSchema,
  reason: z.string().max(MAX_REASON_LENGTH),
  source_ref: z.string(),
});
export type ProductRegistrationDocumentCheck = z.infer<
  typeof ProductRegistrationDocumentCheckSchema
>;
